import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';

interface LiveCamCellProps {
  cam: {
    id: string;
    name: string;
    provider: string;
    video_id: string;
  };
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
}

export default function LiveCamCell({ cam, apiUrl, authHeaders }: LiveCamCellProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const cleanup = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (cam.provider === 'youtube') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${apiUrl}/api/livecams/${cam.id}/stream`, { headers: authHeaders() });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (!cancelled) {
          setStreamUrl(data.stream_url);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cam.id, cam.provider, apiUrl, authHeaders]);

  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;
    const video = videoRef.current;
    cleanup();
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
      video.play().catch(() => {});
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 2,
        liveMaxLatencyDurationCount: 5,
        maxBufferLength: 3,
        backBufferLength: 0,
      });
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      hlsRef.current = hls;
    }
    return cleanup;
  }, [streamUrl, cleanup]);

  if (cam.provider === 'youtube') {
    return (
      <iframe
        src={`https://www.youtube.com/embed/${cam.video_id}?autoplay=1&mute=1`}
        width="100%"
        height="100%"
        frameBorder="0"
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
        title={cam.name}
        style={{ border: 'none', background: '#000' }}
      />
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
      />
      {loading && (
        <div className="video-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>Loading stream...</span>
        </div>
      )}
    </>
  );
}
